import { RAvailUrlProvider } from '../../common/RAvailUrlProvider';
import { Constants } from '../../common/constants';
import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { FxpHttpService } from '@fxp/fxpservices';
import {
  CalendarRequest,
  DomainDataRequest,
  DomainDataResponse,
  GetPendingCalendarItemsRequest,
  GetPendingCalendarItemsResponse,
  GetTeamCalendarResponse,
  GrmApprovalResult,
  GrmEventApprovalRequest,
  NonProjectAssignmentRequest,
} from '../contracts/FMGrmContracts';
import { EngagementDetailsRecord } from '../contracts/DSEContracts';

/**
 * GRM FM dashboard service — talks to the GRM ResourceManagement API (`…/grm/rm`) via FXP
 * `FxpHttpService` (React projects use FxpHttpService directly, not the Angular `HttpAdapter`).
 * Matches the singleton + lazy-FXP-resolution pattern of `DSE.service.ts`, so tests inject a mock
 * `IFMDashboardService` and never touch the FXP shell. Endpoints/verbs are parity of
 * `fmdashboard.service.js` (GetTeamCalendar/GetDomainData/GetMy+DelegatedPendingCalendarItems POST,
 * ApproveRejectCalendarItems PATCH, CreateNonProjectRequestAndSchedules POST).
 */
export interface IFMDashboardService {
  getTeamCalendar: (request: CalendarRequest) => Promise<GetTeamCalendarResponse>;
  getCalendarMasterData: () => Promise<DomainDataResponse>;
  getMyPendingCalendarItems: (
    request: GetPendingCalendarItemsRequest
  ) => Promise<GetPendingCalendarItemsResponse>;
  getDelegatedPendingCalendarItems: (
    request: GetPendingCalendarItemsRequest
  ) => Promise<GetPendingCalendarItemsResponse>;
  approveRejectCalendarItems: (
    requests: GrmEventApprovalRequest[]
  ) => Promise<GrmApprovalResult[]>;
  createNonProjectRequest: (
    request: NonProjectAssignmentRequest
  ) => Promise<unknown>;
  getEngagementDetailsForDSE: (
    demandSourceIds: string[]
  ) => Promise<EngagementDetailsRecord[]>;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const FMDashboardService = (function () {
  let _instance: IFMDashboardService;

  function createInstance(): IFMDashboardService {
    const urlProvider = new RAvailUrlProvider();
    const httpService = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpHttpService
    ) as FxpHttpService;

    const getTeamCalendar = async (
      request: CalendarRequest
    ): Promise<GetTeamCalendarResponse> => {
      const response = await httpService.post(
        urlProvider.GetTeamCalendarUrl(),
        request,
        JSON_HEADERS
      );
      return response?.data as GetTeamCalendarResponse;
    };

    const getCalendarMasterData = async (): Promise<DomainDataResponse> => {
      const payload: DomainDataRequest =
        Constants.GrmApiConstants.calendarMasterDataPayload;
      const response = await httpService.post(
        urlProvider.GetDomainDataUrl(),
        payload,
        JSON_HEADERS
      );
      // GetDomainData nests results under `data.DomainDataResult`.
      return response?.data as DomainDataResponse;
    };

    const getMyPendingCalendarItems = async (
      request: GetPendingCalendarItemsRequest
    ): Promise<GetPendingCalendarItemsResponse> => {
      const response = await httpService.post(
        urlProvider.GetMyPendingCalendarItemsUrl(),
        request,
        JSON_HEADERS
      );
      return response?.data as GetPendingCalendarItemsResponse;
    };

    const getDelegatedPendingCalendarItems = async (
      request: GetPendingCalendarItemsRequest
    ): Promise<GetPendingCalendarItemsResponse> => {
      const response = await httpService.post(
        urlProvider.GetDelegatedPendingCalendarItemsUrl(),
        request,
        JSON_HEADERS
      );
      return response?.data as GetPendingCalendarItemsResponse;
    };

    const approveRejectCalendarItems = async (
      requests: GrmEventApprovalRequest[]
    ): Promise<GrmApprovalResult[]> => {
      // Parity: the body is the ARRAY of approval requests; verb is PATCH.
      const response = await httpService.patch(
        urlProvider.ApproveRejectCalendarItemsUrl(),
        requests,
        JSON_HEADERS
      );
      return (response?.data as GrmApprovalResult[]) ?? [];
    };

    const createNonProjectRequest = async (
      request: NonProjectAssignmentRequest
    ): Promise<unknown> => {
      const response = await httpService.post(
        urlProvider.CreateNonProjectRequestUrl(),
        request,
        JSON_HEADERS
      );
      return response?.data;
    };

    const getEngagementDetailsForDSE = async (
      demandSourceIds: string[]
    ): Promise<EngagementDetailsRecord[]> => {
      if (demandSourceIds.length === 0) {
        return [];
      }
      const response = await httpService.post(
        urlProvider.GetEngagementDetailsForDSEUrl(),
        demandSourceIds,
        JSON_HEADERS
      );
      const records = response?.data as EngagementDetailsRecord[];
      return Array.isArray(records) ? records : [];
    };

    return {
      getTeamCalendar,
      getCalendarMasterData,
      getMyPendingCalendarItems,
      getDelegatedPendingCalendarItems,
      approveRejectCalendarItems,
      createNonProjectRequest,
      getEngagementDetailsForDSE,
    };
  }

  return {
    getSingleton: (): IFMDashboardService => {
      if (!_instance) {
        _instance = createInstance();
      }
      return _instance;
    },
  };
})();

export default FMDashboardService;
